import { useEffect, useMemo, useState } from 'react';

import { Box, Button, ButtonGroup, Tooltip } from '@mui/joy';
import { FaArrowLeft } from 'react-icons/fa';
import { IoMdLock } from 'react-icons/io';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { usePermissionsContext } from '../context/permissionsContext';
import { insightsHubCategoriesConfig } from '../pages/insightsHub/constants';
import { usePermissions } from '../utils/hooks/usePermissions';

import InsightsHubSearch from './InsightsHubSearch';
import Paywall from './Paywall';
import RenderIfHasPermission from './RenderIfHasPermission';

export default function InsightsHubLayout() {
  const { hasPermission } = usePermissions();
  const { isFreemiumUser, isCompassEnabled } = usePermissionsContext();
  const hasPermissionForResearchHub = hasPermission('research_hub:research_list');
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const [showBackButton, setShowBackButton] = useState(true);

  const handleNavItem = (key: string) => {
    navigate(`/research-hub/${key}`);
  };

  const handleReturn = () => {
    navigate(-1);
  };

  const hideLayoutNavigation = () => {
    setShowBackButton(false);
  };

  const showLayoutNavigation = () => {
    setShowBackButton(true);
  };

  useEffect(() => {
    setShowBackButton(true);
  }, [slug]);

  const insightsHubCategoriesConfigWithPermissions = useMemo(() => {
    if (isFreemiumUser && !isCompassEnabled) {
      return insightsHubCategoriesConfig?.map((category) => {
        const { key } = category;
        if (key === 'research-updates') {
          return { ...category, showLocked: true };
        } else {
          return category;
        }
      });
    }
    return insightsHubCategoriesConfig;
  }, [insightsHubCategoriesConfig, isFreemiumUser, isCompassEnabled]);

  return (
    <>
      <Paywall
        isLoadingComplete={true}
        showPaywall={!hasPermissionForResearchHub}
        headingText='Access Exclusive Insights in the Research Hub'
        bodyText='Upgrade your subscription to explore Calyx Global’s curated collection of expert blog posts, in-depth resources, and the latest research updates on carbon markets and climate action.'
      />
      <Box paddingBottom={12} maxWidth={'1440px'} margin={'auto'} paddingTop={3} paddingX={2}>
        {showBackButton ? (
          !!slug ? (
            <Box maxWidth={'1117px'} margin={'auto'}>
              <Button
                variant='outlined'
                size='sm'
                startDecorator={<FaArrowLeft />}
                sx={{ marginBottom: 3 }}
                onClick={handleReturn}
              >
                Return
              </Button>
            </Box>
          ) : (
            <Box
              alignItems={'center'}
              gap={{ xs: 2, lg: 4 }}
              width={'100%'}
              marginBottom={3}
              justifyContent={'space-between'}
              gridTemplateColumns={{ xs: '1fr', lg: '5fr 7fr' }}
              display={'grid'}
            >
              <Box>
                <RenderIfHasPermission permission='research_hub:search'>
                  <InsightsHubSearch />
                </RenderIfHasPermission>
              </Box>
              <Box maxWidth={'100vw'} width={{ xs: '100%', lg: '810px' }} overflow={'auto'}>
                <ButtonGroup color='primary' sx={{ width: 'max-content' }}>
                  {insightsHubCategoriesConfigWithPermissions
                    .sort((a, b) => a?.orderIndex - b?.orderIndex)
                    .map(({ key, label, showLocked }) => {
                      const isSelected = category === key;
                      return (
                        <Tooltip
                          title={showLocked ? 'Upgrade for access' : ''}
                          key={key}
                          placement='top'
                        >
                          <Button
                            variant={isSelected ? 'solid' : 'outlined'}
                            sx={{
                              width: 'max-content',
                            }}
                            onClick={() => (showLocked ? navigate('/upgrade') : handleNavItem(key))}
                            key={key}
                            endDecorator={showLocked ? <IoMdLock /> : null}
                          >
                            {label}
                          </Button>
                        </Tooltip>
                      );
                    })}
                </ButtonGroup>
              </Box>
            </Box>
          )
        ) : null}
        <Outlet context={{ hideLayoutNavigation, showLayoutNavigation }} />
      </Box>
    </>
  );
}
